import { useDeferredValue, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, Copy, Link2, LoaderCircle, X } from "lucide-react";
import type { CurrentUser, ShortLink } from "../../shared/types";
import { api, jsonBody } from "../lib/api";
import { Button, Field, Tag, useToast } from "./ui";

export function CreateLinkPanel({
  user,
  userSlugLength,
  vipSlugLength,
  allowUserCustomSlug,
  open,
  onClose,
  initialSlug = "",
  onShowQr,
}: {
  user: CurrentUser;
  userSlugLength: number;
  vipSlugLength: number;
  allowUserCustomSlug: boolean;
  open: boolean;
  onClose: () => void;
  initialSlug?: string;
  onShowQr: (link: ShortLink) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [targetUrl, setTargetUrl] = useState("");
  const [customSlug, setCustomSlug] = useState(initialSlug);
  const [expiryMode, setExpiryMode] = useState<"default" | "never" | "30d" | "90d" | "custom">("default");
  const [expiresAt, setExpiresAt] = useState("");
  const [created, setCreated] = useState<ShortLink | null>(null);
  const deferredSlug = useDeferredValue(customSlug.trim());
  const isVip = user.role === "user" && user.tier === "vip";
  const isAdmin = user.role === "super_admin" || user.role === "admin";
  const canCustomize = isAdmin || isVip || allowUserCustomSlug;

  useEffect(() => {
    // The panel stays mounted while closed; hydrate an optional slug selected from the pool.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open) setCustomSlug(initialSlug);
  }, [initialSlug, open]);

  const slugQuery = useQuery({
    queryKey: ["slug-check", deferredSlug],
    queryFn: () => api<{ available: boolean; reason: string | null }>(`/api/slugs/${encodeURIComponent(deferredSlug)}`),
    enabled: open && canCustomize && deferredSlug.length > 0,
    staleTime: 5_000,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () =>
      api<ShortLink>("/api/links", {
        method: "POST",
        ...jsonBody({
          targetUrl,
          ...(customSlug.trim() ? { customSlug: customSlug.trim() } : {}),
          expiryMode,
          ...(expiryMode === "custom" && expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
        }),
      }),
    async onSuccess(link) {
      setCreated(link);
      setTargetUrl("");
      setCustomSlug("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["links"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["pool"] }),
      ]);
      toast.push(link.status === "pending" ? "短链已创建并进入审核" : "短链已创建");
    },
    onError: toast.fromError,
  });

  const close = () => {
    setCreated(null);
    onClose();
  };

  if (!open) return null;
  return (
    <section className="create-panel" aria-label="创建短链">
      <header className="create-panel__header">
        <div>
          <span className="eyebrow">CREATE · INLINE FLOW</span>
          <h2>{created ? "短链已就绪" : "粘贴目标，分配一个短地址"}</h2>
        </div>
        <button className="icon-button" type="button" onClick={close} aria-label="关闭创建面板"><X size={18} /></button>
      </header>
      {created ? (
        <div className="create-success">
          <div className="create-success__mark"><Check size={26} /></div>
          <div className="create-success__copy">
            <span className="eyebrow">{created.status === "pending" ? "IN REVIEW" : "ACTIVE NOW"}</span>
            <strong>{created.shortUrl}</strong>
            <p>{created.status === "pending" ? "目标域名不在白名单内，审核通过后自动生效。" : created.targetUrl}</p>
          </div>
          <div className="create-success__actions">
            <Button
              variant="primary"
              onClick={() => void navigator.clipboard.writeText(created.shortUrl).then(() => toast.push("短链已复制"), toast.fromError)}
            >
              <Copy size={15} />复制
            </Button>
            <Button onClick={() => onShowQr(created)}>查看二维码 <ArrowRight size={15} /></Button>
            <Button variant="ghost" onClick={() => setCreated(null)}>继续创建</Button>
          </div>
        </div>
      ) : (
        <form
          className="create-form"
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate();
          }}
        >
          <Field label="目标网址 · TARGET URL" hint="仅支持完整的 http(s) 地址，片段标识不会写入短链。">
            <div className="input-with-icon">
              <Link2 size={16} />
              <input className="input" type="url" required value={targetUrl} onChange={(event) => setTargetUrl(event.target.value)} placeholder="https://example.com/your/long/path" autoFocus />
            </div>
          </Field>
          <div className="create-form__row">
            <Field label="短链后缀 · SLUG" hint={isVip ? `VIP 可指定正好 ${vipSlugLength} 位；管理员号池中的品牌词与字母组合除外。` : canCustomize ? `留空自动生成；普通用户至少 ${userSlugLength} 位，极短号池仅管理员可用。` : "系统将分配不易混淆的随机后缀。"}>
              <div className="slug-field">
                <span>g2.al/</span>
                <input
                  className="input"
                  value={customSlug}
                  onChange={(event) => setCustomSlug(event.target.value)}
                  placeholder={canCustomize ? "留空随机" : "自动分配"}
                  disabled={!canCustomize}
                  pattern="[A-Za-z0-9_-]+"
                  minLength={isVip ? vipSlugLength : undefined}
                  maxLength={isVip ? vipSlugLength : 32}
                />
                {customSlug && canCustomize ? (
                  <span className="slug-field__state">
                    {slugQuery.isFetching ? <LoaderCircle size={14} className="spin" /> : slugQuery.data?.available ? <Tag tone="accent">可用</Tag> : <Tag tone="outline">{slugQuery.data?.reason ?? "不可用"}</Tag>}
                  </span>
                ) : null}
              </div>
            </Field>
            <Field label="有效期 · EXPIRY">
              <select className="input select" value={expiryMode} onChange={(event) => setExpiryMode(event.target.value as typeof expiryMode)}>
                <option value="default">系统默认</option>
                <option value="30d">30 天</option>
                <option value="90d">90 天</option>
                {isAdmin ? <option value="never">永不过期</option> : null}
                <option value="custom">指定日期</option>
              </select>
            </Field>
            {expiryMode === "custom" ? (
              <Field label="到期时间 · DATE">
                <input className="input" type="datetime-local" required value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} />
              </Field>
            ) : null}
          </div>
          <footer className="create-form__footer">
            <p><span className="eyebrow">RULE</span> 用户链接按域名规则自动生效或进入待审；管理员链接直接生效。</p>
            <Button
              variant="primary"
              type="submit"
              disabled={mutation.isPending || !targetUrl || Boolean(customSlug && slugQuery.data && !slugQuery.data.available)}
            >
              {mutation.isPending ? <LoaderCircle size={15} className="spin" /> : <ArrowRight size={15} />}
              创建短链
            </Button>
          </footer>
        </form>
      )}
    </section>
  );
}
